import React, { useEffect, useState, useMemo, ReactNode } from 'react';

import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Cookies from 'universal-cookie';
import getMenu from 'utils/getMenu';

import FooterCompact from 'components/FooterCompact/FooterCompact';
import FooterStandard from 'components/FooterStandard/FooterStandard';
import { useApp } from 'contexts/appContext';
import { FooterType, IFooterType } from 'models/footer';
import { IHubspotForm } from 'models/hubspotForm';
import { IMenu } from 'models/menu';

import CookieAlert from '../components/CookieAlert/CookieAlert';
import FixedTopBanner from '../components/FixedTopBanner/FixedTopBanner';
import HeaderMenu from '../components/HeaderMenu/HeaderMenu';

const cookies = new Cookies();

const renderFooterByType = (
  menu: IMenu[],
  type?: IFooterType,
  hubspotForm?: IHubspotForm,
  onClickCallToAction?: () => void,
  callToActionDisabled?: boolean,
): JSX.Element => {
  switch (type) {
    case FooterType.COMPACT:
      return <FooterCompact menu={menu} />;
    case FooterType.STANDARD:
    default:
      return (
        <FooterStandard
          callToActionDisabled={callToActionDisabled}
          onClickCallToAction={onClickCallToAction}
          menu={menu}
          hubspotForm={hubspotForm}
        />
      );
  }
};

type MainLayoutProps = {
  children?: ReactNode; // Add children explicitly for React 18
  callToActionDisabled?: boolean;
  onClickCallToAction?(): void;
  className?: string;
  displayFooter?: boolean;
  footerType?: IFooterType;
  footerHubspotForm?: IHubspotForm;
};

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  callToActionDisabled,
  onClickCallToAction,
  className,
  displayFooter = false,
  footerType,
  footerHubspotForm,
}) => {
  const [cookieAccepted, setCookieAccepted] = useState(false);
  const [showTopBanner, setShowTopBanner] = useState(false);
  const { donationCampaigns, menuStructure, footerMenuStructure, xmasBanner } =
    useApp();

  useEffect(() => {
    setShowTopBanner(!cookies.get('hide-top-banner'));
    setCookieAccepted(!!cookies.get('slsf-cookie-accept'));
  }, []);

  const handleClose = () => {
    setCookieAccepted(true);
    cookies.set('slsf-cookie-accept', true);
  };

  const handleCloseTopBanner = () => {
    setShowTopBanner(false);
    cookies.set('hide-top-banner', true);
  };

  const menu = useMemo(
    () => getMenu(menuStructure, donationCampaigns),
    [menuStructure, donationCampaigns],
  );
  const footerMenu = useMemo(
    () => [
      ...getMenu(footerMenuStructure),
      {
        label: 'Site Map',
        pathname: '/site-map',
      },
    ],
    [footerMenuStructure],
  );

  return (
    <div className={cx('main-layout flex-column d-flex', className)}>
      {!cookieAccepted && <CookieAlert onClose={handleClose} />}
      <HeaderMenu
        onClickCallToAction={onClickCallToAction}
        callToActionDisabled={callToActionDisabled}
        menu={menu}
      />
      {showTopBanner && xmasBanner && !isEmpty(xmasBanner) && (
        <FixedTopBanner topBanner={xmasBanner} onClose={handleCloseTopBanner} />
      )}
      <div className="main-layout-content flex-grow-1 position-relative">
        {children}
      </div>
      {displayFooter &&
        renderFooterByType(
          footerMenu,
          footerType,
          footerHubspotForm,
          onClickCallToAction,
          callToActionDisabled,
        )}
    </div>
  );
};

export default MainLayout;
